import { money, fileUrl } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/shared";
import { ApplicantCompare } from "@/components/ApplicantCompare";
import { FileText, Check, X, ScrollText, Trash2, ShieldCheck, Sparkles, BadgeCheck } from "lucide-react";

const PURGE_DAYS = 30;
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

export const ApplicationsTab = ({
  applications, properties, leases = [], decide, printApplication, openDecline, openLeaseFor,
  deleteApplication, deleteSignedApplication, bulkPurgeApplications,
  autoPurgeDays = 0, saveAutoPurge, onSuggest,
}) => {
  if (applications.length === 0) {
    return <div className="bg-white border border-line rounded-xl"><EmptyState icon={FileText} title="No applications yet" /></div>;
  }
  const groups = {};
  applications.filter((a) => a.status === "pending" && !a.purged).forEach((a) => {
    (groups[a.property_id] = groups[a.property_id] || []).push(a);
  });
  const compareGroups = Object.values(groups).filter((g) => g.length >= 2);
  const purgeableOld = applications.filter((a) => !a.purged && daysSince(a.created_at) >= PURGE_DAYS).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-line rounded-xl px-4 py-3">
        <p className="text-sm text-mute">
          <ShieldCheck className="h-4 w-4 inline -mt-0.5 mr-1 text-forest" />
          Purge applicant data once you've downloaded or printed what you need. A name-only stub is kept for your records.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {saveAutoPurge && (
            <label className="flex items-center gap-2 text-sm text-ink">
              <span className="text-mute">Auto-purge declined after</span>
              <select
                data-testid="auto-purge-select"
                value={autoPurgeDays}
                onChange={(e) => saveAutoPurge(Number(e.target.value))}
                className="border border-line rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-forest/30"
              >
                <option value={0}>Off</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </label>
          )}
          {bulkPurgeApplications && (
            <button
              data-testid="bulk-purge-apps"
              onClick={bulkPurgeApplications}
              className="border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-sm hover:bg-red-50 flex items-center gap-1"
            >
              <Trash2 className="h-4 w-4" /> Purge all &gt; 30 days{purgeableOld > 0 ? ` (${purgeableOld})` : ""}
            </button>
          )}
        </div>
      </div>

      {compareGroups.length > 0 && (
        <div data-testid="compare-bar" className="bg-forest/[0.04] border border-forest/20 rounded-xl p-4 flex flex-wrap items-center gap-3">
          <p className="text-sm text-ink font-medium">Multiple applicants for a listing — compare them side by side:</p>
          {compareGroups.map((g) => {
            const prop = properties.find((p) => p.id === g[0].property_id) || {};
            return <ApplicantCompare key={g[0].property_id} group={g} title={g[0].property_title} onDecide={decide} baseRent={prop.monthly_rent || 0} baseDeposit={prop.security_deposit || 0} />;
          })}
        </div>
      )}

      {applications.map((a) => {
        if (a.purged) {
          return (
            <div key={a.id} data-testid={`application-${a.id}`} className="bg-bone/60 border border-line rounded-xl p-5 flex flex-wrap gap-3 items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-ink">{a.renter_name}</p>
                  <StatusBadge status={a.status} />
                </div>
                <p className="text-sm text-mute mt-0.5">{a.property_title} · Applied {fmtDate(a.created_at)}</p>
                <p data-testid={`app-purged-${a.id}`} className="mt-1 text-xs text-mute inline-flex items-center gap-1">
                  <Trash2 className="h-3 w-3" /> Applicant data purged{a.purged_at ? ` on ${fmtDate(a.purged_at)}` : ""}
                </p>
              </div>
            </div>
          );
        }
        const hasLease = leases.some((l) => l.property_id === a.property_id && l.renter_id === a.renter_id);
        return (
          <div key={a.id} data-testid={`application-${a.id}`} className="bg-white border border-line rounded-xl p-5 flex flex-wrap gap-4 items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-ink">{a.renter_name}</p>
                <StatusBadge status={a.status} />
                {a.fee_paid && <StatusBadge status="completed" testid={`fee-paid-${a.id}`} />}
              </div>
              <p className="text-sm text-mute mt-0.5">{a.waived ? a.property_title : `${a.property_title} · ${a.employer} · ${money(a.annual_income)}/yr · Move-in ${a.move_in_date}`}</p>
              {a.waived && (
                <p data-testid={`app-waived-${a.id}`} className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-forest bg-forest/10 rounded-full px-2.5 py-0.5">
                  <BadgeCheck className="h-3.5 w-3.5" /> Pre-qualified via your suggestion · application waived
                </p>
              )}
              {a.notes && <p className="text-sm text-mute mt-1 italic">"{a.notes}"</p>}
              {(a.template_questions || []).length > 0 && ["pending", "approved", "rejected"].includes(a.status) && (
                <div data-testid={`app-answers-${a.id}`} className="mt-2 space-y-1">
                  {a.template_questions.map((q) => {
                    let v = (a.answers || {})[q.id];
                    if (q.type === "yes_no") v = v === true || v === "true" || v === "yes" ? "Yes" : (v === false || v === "false" || v === "no" ? "No" : v);
                    if (v === undefined || v === null || v === "") return null;
                    return <p key={q.id} className="text-xs text-mute"><span className="font-medium text-ink">{q.label}:</span> {String(v)}</p>;
                  })}
                </div>
              )}
              {a.status === "awaiting_fee" && <p className="text-xs text-amber-700 mt-1">Awaiting application fee payment from renter.</p>}
              {a.status === "awaiting_form" && <p className="text-xs text-amber-700 mt-1">Fee paid — waiting for renter to complete the application.</p>}
              {a.signed_application?.path && (
                <div className="mt-2 flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <FileText className="h-4 w-4 text-amber-700 shrink-0" />
                  <span className="text-sm text-ink">Signed application</span>
                  <a data-testid={`app-download-${a.id}`} href={fileUrl(a.signed_application.path)} target="_blank" rel="noreferrer" className="text-sm text-forest hover:underline font-medium">Download / Print</a>
                  <button data-testid={`app-delete-doc-${a.id}`} onClick={() => deleteSignedApplication(a.id)} className="text-sm text-red-600 hover:underline inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Delete from platform</button>
                  <span className="text-[11px] text-amber-700 w-full">
                    Contains sensitive applicant info — download, then remove it from the platform.
                    {daysSince(a.created_at) >= PURGE_DAYS && <span data-testid={`app-purge-nudge-${a.id}`} className="font-semibold"> Stored {daysSince(a.created_at)} days — please remove.</span>}
                  </span>
                </div>
              )}
              {a.signed_application_purged && !a.signed_application && (
                <p data-testid={`app-doc-purged-${a.id}`} className="mt-2 text-xs text-mute inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Signed application downloaded & removed from platform</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              {["pending", "approved", "rejected"].includes(a.status) && (
                <button data-testid={`print-app-${a.id}`} onClick={() => printApplication(a)} className="border border-line text-ink px-3 py-1.5 rounded-lg text-sm hover:bg-bone flex items-center gap-1"><FileText className="h-4 w-4" /> View / Print</button>
              )}
              {a.status === "pending" && (
                <>
                  <button data-testid={`approve-${a.id}`} onClick={() => decide(a.id, "approved")} className="bg-forest text-white px-3 py-1.5 rounded-lg text-sm hover:bg-forest-hover flex items-center gap-1"><Check className="h-4 w-4" /> Approve</button>
                  <button data-testid={`reject-${a.id}`} onClick={() => openDecline(a)} className="border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-sm hover:bg-red-50 flex items-center gap-1"><X className="h-4 w-4" /> Reject</button>
                </>
              )}
              {a.status === "approved" && (
                <button data-testid={`create-lease-${a.id}`} onClick={() => openLeaseFor(a)} className="bg-forest text-white px-3 py-1.5 rounded-lg text-sm hover:bg-forest-hover flex items-center gap-1"><ScrollText className="h-4 w-4" /> Create Lease</button>
              )}
              {onSuggest && !a.waived && ["pending", "approved", "rejected"].includes(a.status) && (
                <button data-testid={`suggest-${a.id}`} onClick={() => onSuggest(a)} title="Suggest other available homes (no re-application needed)" className="border border-forest/40 text-forest px-3 py-1.5 rounded-lg text-sm hover:bg-forest/5 flex items-center gap-1"><Sparkles className="h-4 w-4" /> Suggest other homes</button>
              )}
              {["awaiting_fee", "awaiting_form", "pending", "approved", "rejected"].includes(a.status) && (
                <button
                  data-testid={`purge-app-${a.id}`}
                  onClick={() => deleteApplication(a.id)}
                  title={hasLease ? "A lease exists for this applicant — purging won't affect it" : "Purge applicant data"}
                  className="border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-sm hover:bg-red-50 flex items-center gap-1"
                >
                  <Trash2 className="h-4 w-4" /> Delete &amp; purge{hasLease ? " *" : ""}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
